import { z } from 'zod';

const pct = z.number().min(0).max(100);

export const RuleSetSchema = z.object({
  profitTargetPct: pct.positive().nullable(),
  maxDailyLossPct: pct.positive().nullable(),
  dailyLossBasis: z.enum(['balance', 'equity', 'max_of_balance_equity']),
  maxLossPct: pct.positive(),
  maxLossType: z.enum(['static', 'trailing']),
  minTradingDays: z.number().int().min(0).max(365),
  maxCalendarDays: z.number().int().positive().max(3650).nullable(),
}).refine((r) => r.maxDailyLossPct === null || r.maxDailyLossPct <= r.maxLossPct, {
  message: 'Daily loss limit cannot be larger than the max loss limit',
  path: ['maxDailyLossPct'],
});

export const PhaseSchema = z.object({
  key: z.string().regex(/^[a-z0-9-]{1,24}$/, 'Use lowercase letters, numbers and dashes'),
  name: z.string().min(1).max(40),
  funded: z.boolean().optional(),
  rules: RuleSetSchema,
});

/** What staff send when creating or editing a plan. id/version/updatedAt are managed by the store. */
export const PlanInputSchema = z.object({
  id: z.string().regex(/^[a-z0-9-]{3,48}$/, 'Use 3–48 lowercase letters, numbers and dashes'),
  brand: z.enum(['prop', 'futures', 'perps', 'predict', 'fx']),
  name: z.string().min(1).max(40),
  tag: z.string().min(1).max(40),
  description: z.string().max(200).default(''),
  phases: z.array(PhaseSchema).min(1).max(5),
  pricesUsdCents: z.record(z.string().regex(/^\d+$/), z.number().int().positive())
    .refine((p) => Object.keys(p).length > 0, 'Add at least one account size'),
  rewardSplitPct: pct.positive(),
  leverage: z.number().int().min(1).max(1000),
  payout: z.object({
    firstPayoutAfterDays: z.number().int().min(0).max(365),
    cycleDays: z.number().int().min(1).max(365),
    minProfitableDays: z.number().int().min(0).max(60),
    minPayoutUsd: z.number().min(0),
  }),
  active: z.boolean().default(true),
  final: z.boolean().default(false),
}).superRefine((p, ctx) => {
  const last = p.phases[p.phases.length - 1];
  if (!last?.funded) ctx.addIssue({ code: 'custom', path: ['phases'], message: 'The last phase must be the funded stage' });
  p.phases.forEach((ph, i) => {
    if (i < p.phases.length - 1 && ph.funded) ctx.addIssue({ code: 'custom', path: ['phases', i, 'funded'], message: 'Only the last phase can be funded' });
    if (!ph.funded && ph.rules.profitTargetPct === null) ctx.addIssue({ code: 'custom', path: ['phases', i, 'rules', 'profitTargetPct'], message: 'Evaluation phases need a profit target' });
  });
  const keys = p.phases.map((ph) => ph.key);
  if (new Set(keys).size !== keys.length) ctx.addIssue({ code: 'custom', path: ['phases'], message: 'Phase keys must be unique' });
});

export type PlanInput = z.input<typeof PlanInputSchema>;
